import { useEffect, useRef, useState } from 'react';

type UseLazyPreviewInScrollContainerOptions = {
    rootMargin?: string;
    threshold?: number;
};

const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
    let current = element?.parentElement ?? null;

    while (current && current !== document.body) {
        const { overflowY } = window.getComputedStyle(current);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return current;
        }
        current = current.parentElement;
    }

    return null;
};

const useLazyPreviewInScrollContainer = <T extends HTMLElement>({
    rootMargin = '400px 0px',
    threshold = 0.01,
}: UseLazyPreviewInScrollContainerOptions = {}) => {
    const previewRef = useRef<T | null>(null);
    const [shouldRenderPreview, setShouldRenderPreview] = useState(false);

    useEffect(() => {
        if (shouldRenderPreview) {
            return;
        }

        const previewElement = previewRef.current;

        if (!previewElement) {
            return;
        }

        if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
            setShouldRenderPreview(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldRenderPreview(true);
                    observer.disconnect();
                }
            },
            {
                root: findScrollParent(previewElement),
                rootMargin,
                threshold,
            },
        );

        observer.observe(previewElement);

        return () => observer.disconnect();
    }, [rootMargin, shouldRenderPreview, threshold]);

    return {
        previewRef,
        shouldRenderPreview,
    };
};

export default useLazyPreviewInScrollContainer;
